import { Box, Center, Checkbox, HStack, Input, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useInViewport } from "react-in-viewport"
import { PhoneDeviceStatus, PhoneDevicesByFilterQueryVariables, usePhoneDevicesByFilterQuery } from "../../graphql"

export type PhoneDeviceSelectorProps = {
	onUpdate: (phoneDeviceId?: string) => void
	value?: string
}

export const PhoneDeviceSelector: React.FC<PhoneDeviceSelectorProps> = ({ value, onUpdate }) => {
	const [variables, setVariables] = useState<PhoneDevicesByFilterQueryVariables>({
		filter: { keyword: "", status: PhoneDeviceStatus.Available },
		pagination: { limit: 20, page: 1 },
	})

	const [{ data, fetching, error }] = usePhoneDevicesByFilterQuery({ variables })

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.phoneDevicesByFilter.hasNextPage) {
			setVariables((prev) => ({
				...prev,
				pagination: {
					...prev.pagination,
					page: (prev.pagination.page || 0) + 1,
				},
			}))
		}
	}, [data, fetching])

	const ref = useRef()

	const { inViewport } = useInViewport(ref as any, { threshold: 0.25 })

	useEffect(() => {
		if (inViewport) {
			nextPage()
		}
	}, [inViewport, nextPage])

	const [keyword, setKeyword] = useState("")

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setVariables((prev) => ({
				...prev,
				filter: {
					...prev.filter,
					keyword,
					status: PhoneDeviceStatus.Available,
				},
				pagination: { ...prev.pagination, page: 1 },
			}))
		}, 400)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const handleChange = (phoneDeviceId: string, isChecked: boolean) => {
		if (isChecked) {
			onUpdate(phoneDeviceId)
		} else {
			onUpdate(undefined)
		}
	}

	const isPhoneDeviceSelected = useCallback((phoneDeviceId: string) => value === phoneDeviceId, [value])
	const isSelectionActive = useMemo(() => Boolean(value), [value])

	const availableDevices = useMemo(() => data?.phoneDevicesByFilter.phoneDevices || [], [data])

	return (
		<VStack w="full" align="stretch">
			<HStack w="full" flexWrap="wrap" justify="space-between">
				<Input flex="1" variant="filled" bgColor="grayscale.input-background" placeholder="Search" _placeholder={{ color: "grayscale.placeholer" }} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
			</HStack>
			{fetching && !availableDevices.length ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading phone devices</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : !availableDevices.length ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						No available phone devices found.
					</Text>
				</Center>
			) : (
				<VStack w="full" align="stretch" h="full" maxH={{ base: "sm", xl: "lg" }} overflowY="auto" spacing={0}>
					<Table>
						<Thead pos="sticky" top="0" w="full" zIndex={10} bgColor="white">
							<Tr>
								<Th>
									<Checkbox
										isChecked={isSelectionActive}
										onChange={() => {
											onUpdate(undefined)
										}}
									>
										<Text fontSize="xs" textTransform="lowercase">
											{value ? 1 : 0} selected
										</Text>
									</Checkbox>
								</Th>
								<Th>Device</Th>
								<Th>ID</Th>
							</Tr>
						</Thead>
						<Tbody>
							{availableDevices.map((device) => (
								<Tr key={device._id}>
									<Td>
										<Checkbox isChecked={isPhoneDeviceSelected(device._id)} onChange={(e) => handleChange(device._id, e.target.checked)} />
									</Td>
									<Td>{device.label.name}</Td>
									<Td>{device.deviceId}</Td>
								</Tr>
							))}
							<Box w="full" h="2" ref={ref as any} />
						</Tbody>
					</Table>
					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more phone devices</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			)}
		</VStack>
	)
}
